// Who registered at an activation, and one of them in full, as values this
// console can draw.
//
// THE ONE RULE THIS FILE IS WRITTEN AROUND. Every sentence the service sends
// about what it does not know travels through here untouched. `subset_note`,
// `consent_note`, `access_note`, the `says` on a passcode account and every
// `cannot_answer` entry are carried as strings and never summarised, shortened
// or replaced with a console's own wording. docs/console-people-activity.md is
// an audit of personal data, and the four gaps it names are all absences that
// draw as a reassuring answer: a missing deliverability draws as fine, a missing
// withhold flag draws as in every file. The service names them so that a screen
// can state them. A console that reworded them would be a second opinion about
// what this service knows, and the two would disagree after the next change.
//
// So the parsing here is deliberately dull about the honest strings: they are
// read, or their absence is a response this console will not draw. It is only
// the person-shaped fields that get the tolerant treatment, and only downwards,
// into null.
//
// No policy about who may read this. The service refuses a client viewer in its
// own words and this file maps the status to the same Failure vocabulary the
// reports use, which is admin.ts's rule and README.md's first one: the console
// renders decisions, it does not make them.

import type { AttendeeSortKey } from "../../../operator/src/attendees.js";
import { attendeeUrl, attendeesUrl } from "./endpoints.js";
import { authenticated, failureFor, type ApiOptions, type Read } from "./client.js";
import type { SessionToken } from "../session/store.js";

/**
 * How big a page this console asks for before anybody touches the pager.
 *
 * Its own number rather than `ATTENDEE_PAGE_DEFAULT` from xp/operator, and the
 * duplication is deliberate. endpoints.ts explains why both bounds are always
 * on the wire: the page is the unit `report_access` counts a disclosure in, so
 * the console has to know how big its own recorded read was. Importing the
 * service's default would be this console agreeing in advance to whatever that
 * default becomes, and the figure filed against somebody's sign-in would then
 * change without a screen changing.
 *
 * Twenty five rather than the service's fifty, and it is one of the three sizes
 * the shared pager offers, so the control on the screen and the request agree
 * from the first render. It is also the smaller disclosure, which is the right
 * way round for the default on a screen full of named people.
 */
export const PEOPLE_PER_PAGE = 25;

/** A fact the design asked for and this service does not hold, in the service's words. */
export type AttendeeGap = {
  readonly what: string;
  readonly why: string;
};

/**
 * The latest word per purpose.
 *
 * Two words and not three. exportRule.ts has a `never`, and the service's own
 * map never carries it: `consentStates` writes granted or withdrawn and leaves
 * a purpose absent otherwise, because there is no event for a refusal. An
 * absent purpose is unknown, and the note beside it says so. A third word here
 * would be the permissive reading of that absence, invented in a browser.
 */
export type ConsentWord = "granted" | "withdrawn";

export type ConsentMap = Readonly<Record<string, ConsentWord>>;

export type AttendeeLine = {
  readonly attendeeRef: string;
  readonly registeredAt: string;
  /** stand_tablet, self_serve_web or onsite_web. Three, where the design drew two. */
  readonly captureChannel: string | null;
  readonly identityAssurance: string | null;
  /** Null means no address was ever proved, which is not an address that failed. */
  readonly emailVerifiedAt: string | null;
  readonly displayName: string | null;
  readonly contactEmail: string | null;
  // No telephone number, delivery address or consent map. The service stopped
  // sending them on a list row because nothing here drew them; operator's
  // attendees.ts carries the argument. They are all on the record.
  /** What an Admin has done to this record (152), as it stands now. */
  readonly standing: AttendeeStanding;
};

/**
 * Where a person stands after an Admin's actions (152). Four flags, because
 * they are independent; erased outranks the rest in what is drawn.
 */
export type AttendeeStanding = {
  readonly withheld: boolean;
  readonly blocked: boolean;
  readonly erased: boolean;
  readonly corrected: boolean;
};

/** One thing an Admin did to the record, oldest first. The record of it is the audit. */
export type AttendeeAction = {
  readonly action: "withhold" | "release" | "block" | "unblock" | "correct" | "erase";
  readonly at: string;
  readonly reason: string | null;
  readonly byIdentityId: string;
  /** For a correction, each field moved and from what to what; null once an erasure scrubbed it. */
  readonly correction: Readonly<Record<string, { readonly from: string | null; readonly to: string | null }>> | null;
  readonly scrubbedAt: string | null;
  /** True where an erasure scrubbed the reason this row was given (153). */
  readonly reasonScrubbed: boolean;
};

/** What an erasure reached (153), for an erased person's record. */
export type AttendeeErasure = {
  readonly spineRows: number;
  readonly entryGuests: number;
  readonly boothMedia: number;
  readonly boothBytesDeleted: number | null;
  readonly devicesTold: number;
  readonly rideState: "pending" | "done" | "not_configured";
  readonly rideAttempts: number;
  readonly rideErased: number | null;
};

const NOT_ACTED_ON: AttendeeStanding = { withheld: false, blocked: false, erased: false, corrected: false };

export type AttendeePagePosition = {
  readonly limit: number;
  readonly offset: number;
  readonly returned: number;
  /** Everybody in the subset, not everybody on this page. */
  readonly total: number;
  readonly more: boolean;
};

export type AttendeePage = {
  readonly activationId: string;
  readonly asAt: string;
  readonly accessNote: string;
  readonly attendees: readonly AttendeeLine[];
  readonly page: AttendeePagePosition;
  readonly subsetNote: string;
  readonly cannotAnswer: readonly AttendeeGap[];
};

export type AttendeeWording = {
  readonly wordingHash: string;
  readonly locale: string;
  readonly body: string;
};

export type AttendeeConsentEvent = {
  readonly at: string;
  readonly event: "consent_captured" | "consent_withdrawn";
  readonly purpose: string | null;
  readonly consentVersionId: string | null;
  readonly method: string | null;
  readonly captureChannel: string | null;
  /** The exact text shown, or null. Null on a withdrawal, which cites no paragraph. */
  readonly wording: AttendeeWording | null;
};

export type AttendeePasscode = {
  readonly verificationId: string;
  readonly issuedAt: string;
  /** When it was due to stop working. A scheduled moment, not a recorded one. */
  readonly expiresAt: string;
  /** When the mailbox was proved. Null means this passcode was never spent. */
  readonly consumedAt: string | null;
  readonly supersededAt: string | null;
  readonly ticketSpentAt: string | null;
};

/**
 * What could be found out about this person's passcodes, or why nothing was
 * looked for.
 *
 * THE UNION IS KEPT, and it is the one shape in this file that must not be
 * flattened. attendees.ts says why in as many words: an instance with no
 * attendee pepper cannot compute the digest that is the only route from a person
 * to their passcode rows, and a registration with no address has nothing to
 * compute from. Collapsing either into an empty list would tell a reader this
 * person was never sent a passcode, which is a different fact and a worse one.
 */
export type AttendeePasscodeAccount =
  | { readonly kind: "not_looked_up"; readonly why: string; readonly says: string }
  | { readonly kind: "read"; readonly passcodes: readonly AttendeePasscode[]; readonly says: string };

export type AttendeeRegistration = {
  readonly registeredAt: string;
  readonly captureChannel: string | null;
  /** Where the person was, which is not the channel. A crew member handing over an iPad is both. */
  readonly surface: string | null;
  readonly captureMode: string | null;
  /**
   * Who helped, where anybody did.
   *
   * The nearest fact to the design's Crew column and deliberately not the same
   * one: a crew member assisted this registration, which is not the same as the
   * person registering being crew. The gap list says that too.
   */
  readonly assistedBy: string | null;
  readonly identityAssurance: string | null;
  readonly emailVerifiedAt: string | null;
  readonly fieldSetVersion: number | null;
  readonly capturedUnderConfigVersion: number | null;
  readonly displayName: string | null;
  readonly contactEmail: string | null;
  readonly deliverToEmail: string | null;
  readonly phone: string | null;
  /** Whatever this activation's form asked for, as it was captured. */
  readonly fields: readonly { readonly name: string; readonly value: string }[];
};

export type AttendeeRecord = {
  readonly activationId: string;
  readonly attendeeRef: string;
  readonly asAt: string;
  readonly accessNote: string;
  readonly registration: AttendeeRegistration;
  readonly consent: ConsentMap;
  readonly consentEvents: readonly AttendeeConsentEvent[];
  readonly passcodes: AttendeePasscodeAccount;
  readonly consentNote: string;
  readonly standing: AttendeeStanding;
  readonly actions: readonly AttendeeAction[];
  /** The service's sentence about the standing. */
  readonly standingNote: string;
  readonly erasure: AttendeeErasure | null;
  readonly cannotAnswer: readonly AttendeeGap[];
};

/**
 * One page of the people at an activation.
 *
 * The page bounds are arguments rather than state held here, so that the pager
 * on the screen and the numbers in the `report_access` row this read files are
 * the same numbers.
 */
export async function readAttendees(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
  offset: number,
  limit: number = PEOPLE_PER_PAGE,
  /**
   * The column and direction the reader pressed, or null for the order people
   * arrived in. The service orders the whole subset and hands back this page of
   * it, so the order covers every page and not the one on screen.
   */
  order: { readonly key: AttendeeSortKey; readonly dir: "asc" | "desc" } | null = null,
): Promise<Read<AttendeePage>> {
  const answer = await authenticated(options, token, {
    path: attendeesUrl(activationId, limit, offset, order),
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...failureFor(answer) };

  const raw = fields(answer.body);
  const rows = raw["attendees"];
  const position = asPagePosition(raw["page"], limit, offset);
  const subsetNote = text(raw["subset_note"]);
  const accessNote = text(raw["access_note"]);
  const gaps = asGaps(raw["cannot_answer"]);

  // THE NOTES ARE REQUIRED AND THAT IS THIS FILE'S SUBSTANCE. A page of names
  // drawn without `subset_note` is a page whose edges a reader cannot see, and
  // without `cannot_answer` it is a page that has quietly stopped mentioning
  // four things it cannot answer. Both would draw perfectly. Defaulting them to
  // an empty string would be this console deciding that the honest sentences are
  // decoration; refusing is the only reading that keeps them load-bearing.
  if (!Array.isArray(rows) || position === null || subsetNote === null || accessNote === null) {
    return { ok: false, ...unreadable("list of people") };
  }
  if (gaps === null) return { ok: false, ...unreadable("list of people") };

  const attendees: AttendeeLine[] = [];
  for (const entry of rows) {
    const line = asLine(entry);
    if (line === null) return { ok: false, ...unreadable("list of people") };
    attendees.push(line);
  }

  return {
    ok: true,
    value: {
      activationId: text(raw["activation_id"]) ?? activationId,
      asAt: text(raw["as_at"]) ?? "",
      accessNote,
      attendees,
      page: position,
      subsetNote,
      cannotAnswer: gaps,
    },
  };
}

/**
 * One person's record.
 *
 * A 404 from this endpoint arrives as `unavailable` through `failureFor`, which
 * is where it belongs: the service's own sentence says a reference belongs to
 * one activation and means nothing at another, so the screen shows that sentence
 * rather than a console guessing which of the two happened.
 */
export async function readAttendee(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
  attendeeRef: string,
): Promise<Read<AttendeeRecord>> {
  const answer = await authenticated(options, token, {
    path: attendeeUrl(activationId, attendeeRef),
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...failureFor(answer) };

  const raw = fields(answer.body);
  const registration = asRegistration(raw["registration"]);
  const passcodes = asPasscodeAccount(raw["passcodes"]);
  const consentNote = text(raw["consent_note"]);
  const accessNote = text(raw["access_note"]);
  const gaps = asGaps(raw["cannot_answer"]);
  const ref = text(raw["attendee_ref"]);

  if (
    registration === null ||
    passcodes === null ||
    consentNote === null ||
    accessNote === null ||
    gaps === null ||
    ref === null
  ) {
    return { ok: false, ...unreadable("person's record") };
  }

  const events: AttendeeConsentEvent[] = [];
  const eventRows = raw["consent_events"];
  if (!Array.isArray(eventRows)) return { ok: false, ...unreadable("person's record") };
  for (const entry of eventRows) {
    const event = asConsentEvent(entry);
    if (event === null) return { ok: false, ...unreadable("person's record") };
    events.push(event);
  }

  return {
    ok: true,
    value: {
      activationId: text(raw["activation_id"]) ?? activationId,
      attendeeRef: ref,
      asAt: text(raw["as_at"]) ?? "",
      accessNote,
      registration,
      consent: asConsentMap(raw["consent"]),
      consentEvents: events,
      passcodes,
      consentNote,
      standing: asStanding(raw["standing"]),
      actions: asActions(raw["actions"]),
      standingNote: text(raw["standing_note"]) ?? "",
      erasure: asErasure(raw["erasure"]),
      cannotAnswer: gaps,
    },
  };
}

/**
 * The standing, or nothing done where a service predating 152 sent none.
 * Read as nothing done rather than refused: an older service has no person
 * actions, so "nothing has been done" is the true answer from it.
 */
function asStanding(value: unknown): AttendeeStanding {
  const row = fields(value);
  if (typeof row["withheld"] !== "boolean") return NOT_ACTED_ON;
  return {
    withheld: row["withheld"] === true,
    blocked: row["blocked"] === true,
    erased: row["erased"] === true,
    corrected: row["corrected"] === true,
  };
}

function asErasure(value: unknown): AttendeeErasure | null {
  const row = fields(value);
  const state = row["ride_state"];
  if (state !== "pending" && state !== "done" && state !== "not_configured") return null;
  const n = (key: string) => (typeof row[key] === "number" ? (row[key] as number) : 0);
  return {
    spineRows: n("spine_rows"),
    entryGuests: n("entry_guests"),
    boothMedia: n("booth_media"),
    boothBytesDeleted: typeof row["booth_bytes_deleted"] === "number" ? (row["booth_bytes_deleted"] as number) : null,
    devicesTold: n("devices_told"),
    rideState: state,
    rideAttempts: n("ride_attempts"),
    rideErased: typeof row["ride_erased"] === "number" ? (row["ride_erased"] as number) : null,
  };
}

const ACTION_WORDS = new Set(["withhold", "release", "block", "unblock", "correct", "erase"]);

function asActions(value: unknown): AttendeeAction[] {
  if (!Array.isArray(value)) return [];
  const out: AttendeeAction[] = [];
  for (const entry of value) {
    const row = fields(entry);
    const action = text(row["action"]);
    const at = text(row["at"]);
    const by = text(row["by_identity_id"]);
    if (action === null || !ACTION_WORDS.has(action) || at === null || by === null) continue;
    const raw = row["correction"];
    const correction: Record<string, { from: string | null; to: string | null }> | null =
      raw !== null && typeof raw === "object" && !Array.isArray(raw) ? {} : null;
    if (correction !== null) {
      for (const [field, change] of Object.entries(raw as Record<string, unknown>)) {
        const moved = fields(change);
        correction[field] = { from: text(moved["from"]), to: text(moved["to"]) };
      }
    }
    out.push({
      action: action as AttendeeAction["action"],
      at,
      reason: text(row["reason"]),
      byIdentityId: by,
      correction,
      scrubbedAt: text(row["scrubbed_at"]),
      reasonScrubbed: row["reason_scrubbed"] === true,
    });
  }
  return out;
}

// ------------------------------------------------------------------ the shapes

function asLine(value: unknown): AttendeeLine | null {
  const row = fields(value);
  const attendeeRef = text(row["attendee_ref"]);
  const registeredAt = text(row["registered_at"]);
  // Both required. A row without a reference cannot be opened and a row without
  // a moment cannot be placed in the show, and either drawn blank is a person
  // this console is asserting something false about.
  if (attendeeRef === null || registeredAt === null) return null;
  return {
    attendeeRef,
    registeredAt,
    captureChannel: text(row["capture_channel"]),
    identityAssurance: text(row["identity_assurance"]),
    emailVerifiedAt: text(row["email_verified_at"]),
    displayName: text(row["display_name"]),
    contactEmail: text(row["contact_email"]),
    standing: asStanding(row["standing"]),
  };
}

function asPagePosition(value: unknown, limit: number, offset: number): AttendeePagePosition | null {
  const row = fields(value);
  const returned = integer(row["returned"]);
  const total = integer(row["total"]);
  // `total` is what the pager counts in and what tells a reader this page is a
  // slice. Without it there is no honest pager, so there is no page.
  if (returned === null || total === null) return null;
  return {
    limit: integer(row["limit"]) ?? limit,
    offset: integer(row["offset"]) ?? offset,
    returned,
    total,
    more: row["more"] === true,
  };
}

/**
 * The gap list, or the fact that the response did not carry one.
 *
 * Null and not `[]` on a missing or malformed list. An empty array is a
 * statement, and the statement is that this service can answer everything the
 * design asked for, which has never been true. The service holds these frozen
 * and sends them on every response, so an absence is a response this console
 * does not understand.
 */
function asGaps(value: unknown): readonly AttendeeGap[] | null {
  if (!Array.isArray(value)) return null;
  const gaps: AttendeeGap[] = [];
  for (const entry of value) {
    const row = fields(entry);
    const what = text(row["what"]);
    const why = text(row["why"]);
    if (what === null || why === null) return null;
    gaps.push({ what, why });
  }
  return gaps;
}

/** Only the two words the service writes. Anything else is dropped rather than guessed at. */
function asConsentMap(value: unknown): ConsentMap {
  const row = fields(value);
  const map: Record<string, ConsentWord> = {};
  for (const [purpose, word] of Object.entries(row)) {
    if (word === "granted" || word === "withdrawn") map[purpose] = word;
  }
  return map;
}

function asRegistration(value: unknown): AttendeeRegistration | null {
  const row = fields(value);
  const registeredAt = text(row["registered_at"]);
  if (registeredAt === null) return null;
  const contact = fields(row["contact"]);
  return {
    registeredAt,
    captureChannel: text(row["capture_channel"]),
    surface: text(row["surface"]),
    captureMode: text(row["capture_mode"]),
    assistedBy: text(row["assisted_by"]),
    identityAssurance: text(row["identity_assurance"]),
    emailVerifiedAt: text(row["email_verified_at"]),
    fieldSetVersion: integer(row["field_set_version"]),
    capturedUnderConfigVersion: integer(row["captured_under_config_version"]),
    displayName: text(contact["display_name"]),
    contactEmail: text(contact["contact_email"]),
    deliverToEmail: text(contact["deliver_to_email"]),
    phone: text(contact["phone"]),
    fields: asFormFields(row["fields"]),
  };
}

/**
 * This activation's form, as it was captured.
 *
 * `Record<string, unknown>` on the wire, because a field set is whatever that
 * activation asked for, so it is turned into named strings here rather than
 * being indexed into by a screen that would have to guess at types. An absent
 * answer is kept as the empty string and drawn as absent by the screen; the
 * alternative was dropping the row, which would say the form never asked.
 */
function asFormFields(value: unknown): readonly { readonly name: string; readonly value: string }[] {
  const row = fields(value);
  return Object.entries(row).map(([name, raw]) => ({ name, value: printable(raw) }));
}

function printable(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  // An object or a list, which a field set may legitimately hold. Drawn as its
  // own json rather than as [object Object], which tells a reader nothing about
  // their own person's record.
  try {
    return JSON.stringify(value) ?? "";
  } catch {
    return "";
  }
}

function asConsentEvent(value: unknown): AttendeeConsentEvent | null {
  const row = fields(value);
  const at = text(row["at"]);
  if (at === null) return null;
  const event = row["event"] === "consent_withdrawn" ? "consent_withdrawn" : "consent_captured";
  const wording = fields(row["wording"]);
  const hash = text(wording["wording_hash"]);
  const body = text(wording["body"]);
  return {
    at,
    event,
    purpose: text(row["purpose"]),
    consentVersionId: text(row["consent_version_id"]),
    method: text(row["method"]),
    captureChannel: text(row["capture_channel"]),
    wording: hash === null || body === null ? null : { wordingHash: hash, locale: text(wording["locale"]) ?? "", body },
  };
}

function asPasscodeAccount(value: unknown): AttendeePasscodeAccount | null {
  const row = fields(value);
  const says = text(row["says"]);
  if (says === null) return null;

  if (row["kind"] === "not_looked_up") {
    return { kind: "not_looked_up", why: text(row["why"]) ?? "", says };
  }

  if (row["kind"] !== "read") return null;

  const rows = row["passcodes"];
  if (!Array.isArray(rows)) return null;
  const passcodes: AttendeePasscode[] = [];
  for (const entry of rows) {
    const passcode = asPasscode(entry);
    if (passcode === null) return null;
    passcodes.push(passcode);
  }
  return { kind: "read", passcodes, says };
}

function asPasscode(value: unknown): AttendeePasscode | null {
  const row = fields(value);
  const verificationId = text(row["verification_id"]);
  const issuedAt = text(row["issued_at"]);
  const expiresAt = text(row["expires_at"]);
  if (verificationId === null || issuedAt === null || expiresAt === null) return null;
  return {
    verificationId,
    issuedAt,
    expiresAt,
    consumedAt: text(row["consumed_at"]),
    supersededAt: text(row["superseded_at"]),
    ticketSpentAt: text(row["ticket_spent_at"]),
  };
}

// ------------------------------------------------------------- small things

function unreadable(what: string): { readonly kind: "unavailable"; readonly detail: string } {
  return {
    kind: "unavailable",
    detail:
      `The service answered a ${what} this console could not read. Nothing is drawn from it, because a ` +
      `partly understood answer about a person is the one thing worse than none.`,
  };
}

function fields(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** A string, or nothing. Never the empty string, which a screen reads as a value. */
function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * A whole number the service actually sent.
 *
 * `typeof value === "number"` first, and it is not decoration: `Number(null)` is
 * zero and finite, so the shorter version turns an absent total into a page of
 * nobody and reports it as counted. ride.ts's `numberOrNull` carries the long
 * version of this note, and every field that comes through here is a figure a
 * reader would act on.
 */
function integer(value: unknown): number | null {
  if (typeof value === "number") return Number.isSafeInteger(value) ? value : null;
  if (typeof value !== "string" || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}
